import { jsx as _jsx } from "react/jsx-runtime";
import constant from "lodash/constant";
import noop from "lodash/noop";
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { DesktopAppWebSocket } from "~/classes/desktopAppWebsocket/DesktopAppWebSocket";
import { Routes } from "~/constants/routes";
import { QueryParamNames } from "~/constants/search";
import { NEW_TAB } from "~/constants/urls";
import { isDevOrTestStage } from "~/swr-hooks/utils";
import { reportErrorToBugsnag } from "~/utils/ErrorUtils";
const defaultValue = {
    isConnected: constant(false),
    openClipInFinder: noop,
    openBoardInFinder: noop
};
const DesktopAppConnectionContext = /*#__PURE__*/ createContext(defaultValue);
export const DesktopAppConnectionProvider = /*#__PURE__*/ memo((param)=>{
    let { children } = param;
    const isAppConnected = useRef(false);
    const isConnected = useCallback(()=>isAppConnected.current, []);
    const socketConnectionSubscription = useMemo(()=>({
            onConnected: ()=>{
                isAppConnected.current = true;
            },
            onDisconnected: ()=>{
                isAppConnected.current = false;
            },
            onMessage: noop
        }), []);
    const openClipInFinder = useCallback((clip, boardId)=>{
        if (isAppConnected.current) {
            DesktopAppWebSocket.sendMessage({
                type: "open-asset",
                data: {
                    assetId: clip.id,
                    boardId,
                    asset: {
                        id: clip.assetId
                    }
                }
            });
        } else {
            DesktopAppWebSocket.connect(socketConnectionSubscription);
            const searchParams = new URLSearchParams();
            searchParams.set(QueryParamNames.assetId, clip.assetId);
            searchParams.set(QueryParamNames.clipId, clip.id);
            if (!!boardId) {
                searchParams.set(QueryParamNames.boardId, boardId);
            }
            const url = "".concat(window.location.origin, "/").concat(Routes.desktop.openDesktopFile, "?").concat(searchParams.toString());
            window.open(url, NEW_TAB);
        }
    }, [
        socketConnectionSubscription
    ]);
    const openBoardInFinder = useCallback((boardId)=>{
        if (isAppConnected.current) {
            DesktopAppWebSocket.sendMessage({
                type: "open-board",
                data: {
                    boardId
                }
            });
        } else {
            DesktopAppWebSocket.connect(socketConnectionSubscription);
            const searchParams = new URLSearchParams();
            searchParams.set(QueryParamNames.boardId, boardId);
            const url = "".concat(window.location.origin, "/").concat(Routes.desktop.openDesktopFile, "?").concat(searchParams.toString());
            window.open(url, NEW_TAB);
        }
    }, [
        socketConnectionSubscription
    ]);
    useEffect(()=>{
        DesktopAppWebSocket.connect(socketConnectionSubscription);
    }, [
        socketConnectionSubscription
    ]);
    const value = useMemo(()=>({
            isConnected,
            openClipInFinder,
            openBoardInFinder
        }), [
        isConnected,
        openBoardInFinder,
        openClipInFinder
    ]);
    return /*#__PURE__*/ _jsx(DesktopAppConnectionContext.Provider, {
        value: value,
        children: children
    });
});
DesktopAppConnectionProvider.displayName = "DesktopAppConnectionProvider";
export const useDesktopAppConnectionContext = ()=>{
    const context = useContext(DesktopAppConnectionContext);
    if (context === defaultValue) {
        const error = "useDesktopAppConnectionContext used outside of DesktopAppConnectionProvider";
        if (isDevOrTestStage()) {
            throw error;
        } else {
            reportErrorToBugsnag({
                error,
                context: "Invalid usage of DesktopAppConnectionContext"
            });
        }
    }
    return context;
};
